import { Link, Navigate } from "react-router-dom"

import { H1, H2, H5 } from "~/ui-rtk/components/ui/typography"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import {
  SOS_BOOK_A_CALL_URL,
  SOS_UPSELL_BODY,
  SOS_UPSELL_HEADLINE,
} from "~/ui-rtk/constants/sos"
import useAccess from "~/ui-rtk/hooks/access"

const AGENCY_SERVICES = [
  {
    key: "Measure",
    title: "Full-funnel measurement",
    body: "Revenue, paid and organic media, and brand tracking in one model — refreshed six times a week, owned by you.",
  },
  {
    key: "Decide",
    title: "Monthly growth reviews",
    body: "Our analysts walk your model with you: where spend is saturating, which creative is fatiguing, where brand is compounding.",
  },
  {
    key: "Act",
    title: "Done-with-you execution",
    body: "Media-mix shifts, creative briefs, and brand plays — recommended from your data, executed with your team over a shared Slack channel.",
  },
]

/**
 * The single upsell surface (APP-485): SOS_FREE paywall + replacement for the
 * removed /subscribe page. Deliberately no pricing and no self-serve checkout
 * — the only customer path to the paid product is a booked call.
 */
export default function UpgradePage() {
  const { hasAccess, isBillingEnabled, isSosFree } = useAccess()

  // isSosFree must be checked before hasAccess (ACL grants SOS_FREE blanket
  // access, APP-453); hasAccess keeps super admins redirected home.
  if (!isSosFree) {
    if (hasAccess) {
      return <Navigate to="/" replace />
    }
    if (!isBillingEnabled) {
      return <Navigate to={brandRoutes.SETUP.CONNECTORS} replace />
    }
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="mx-auto max-w-256 px-4 py-10 flex flex-col gap-16">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div className="flex flex-col gap-4">
            <H1 className="font-semibold text-4xl md:text-5xl">
              {SOS_UPSELL_HEADLINE}
            </H1>
            <p className="opacity-70 max-w-prose">{SOS_UPSELL_BODY}</p>
            {isSosFree && (
              <Link
                to={brandRoutes.MARKET_SHARE.SHARE_OF_SEARCH}
                className="link"
              >
                Back to your Share of Search report
              </Link>
            )}
          </div>
          <div className="card bg-base-100 shadow-xl border border-basic-blue">
            <div className="card-body items-center text-center gap-2">
              <H2>Book your strategy call</H2>
              <p className="opacity-70">
                30 minutes · with a founder, not a sales rep
              </p>
              <a
                href={SOS_BOOK_A_CALL_URL}
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary btn-wide mt-2"
              >
                Pick a time
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="text-center">
            <H2>What the agency does with your data</H2>
            <p className="opacity-70 mt-2 mx-auto max-w-prose">
              Software shows you the numbers. The agency changes them. Every
              engagement runs on the same platform you&apos;d subscribe to — so
              nothing we do is a black box.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {AGENCY_SERVICES.map(service => (
              <div key={service.key} className="card bg-base-100 shadow-xl">
                <div className="card-body gap-2">
                  <H5 className="text-brand uppercase text-sm">
                    {service.key}
                  </H5>
                  <div className="font-semibold">{service.title}</div>
                  <p className="opacity-70 text-sm">{service.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
